import {
  Box,
  Card,
  Typography,
  Button,
  Toolbar,
  alpha,
} from '@mui/material'
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
} from '@mui/x-data-grid'
import { Download as DownloadIcon } from '@mui/icons-material'
import { useState, useMemo } from 'react'
import { InvoiceRow } from '@/types'
import { formatCurrency, formatDate } from '@/utils/formatters'
import { downloadCSV } from '@/utils/excelParser'

interface InvoiceTableProps {
  invoices: InvoiceRow[]
  isLoading?: boolean
}

export default function InvoiceTable({ invoices, isLoading }: InvoiceTableProps) {
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 })

  // Create rows with stable IDs
  const rows = useMemo(() => {
    return invoices.map((invoice, index) => ({
      ...invoice,
      _id: `invoice-${index}`,
      _rowNumber: index + 1,
    }))
  }, [invoices])

  const columns: GridColDef<InvoiceRow & { _rowNumber: number; _id: string }>[] = useMemo(
    () => [
      {
        field: 'rechnungsnummer',
        headerName: 'Invoice #',
        minWidth: 150,
        flex: 1,
        renderCell: (params: GridRenderCellParams<InvoiceRow & { _rowNumber: number }, 'rechnungsnummer'>) => {
          if (params.value && params.value.trim()) {
            return params.value
          }
          return `#${params.row._rowNumber}`
        },
      },
      {
        field: 'supplierName',
        headerName: 'Supplier',
        minWidth: 200,
        flex: 2,
      },
      {
        field: 'dateInvoice',
        headerName: 'Date',
        minWidth: 120,
        flex: 1,
        renderCell: (params: GridRenderCellParams<InvoiceRow, 'dateInvoice'>) => {
          // @ts-ignore - MUI v7 typing issue
          return formatDate(params.value)
        },
      },
      {
        field: 'netto',
        headerName: 'NETTO',
        minWidth: 130,
        flex: 1,
        type: 'number',
        // @ts-ignore
        valueFormatter: (params) => formatCurrency(params?.value),
        align: 'right',
        headerAlign: 'right',
        renderCell: (params: GridRenderCellParams<InvoiceRow, 'netto'>) => (
          <Typography fontWeight={500} color="primary.main">
            {formatCurrency(typeof params?.value === 'number' ? params.value : null)}
          </Typography>
        ),
      },
      {
        field: 'vatAmount',
        headerName: 'VAT',
        minWidth: 120,
        flex: 1,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        renderCell: (params: GridRenderCellParams<InvoiceRow, 'vatAmount'>) => (
          <Typography variant="body2" color="info.main">
            {formatCurrency(typeof params?.value === 'number' ? params.value : null)}
          </Typography>
        ),
      },
      {
        field: 'taxFreeAmount',
        headerName: 'Tax-Free',
        minWidth: 120,
        flex: 1,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        renderCell: (params: GridRenderCellParams<InvoiceRow, 'taxFreeAmount'>) => (
          <Typography variant="body2" color="warning.main">
            {formatCurrency(typeof params?.value === 'number' ? params.value : null)}
          </Typography>
        ),
      },
      {
        field: 'taxableNetAmount',
        headerName: 'Taxable Net',
        minWidth: 130,
        flex: 1,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        renderCell: (params: GridRenderCellParams<InvoiceRow, 'taxableNetAmount'>) => (
          <Typography variant="body2" color="secondary.main">
            {formatCurrency(typeof params?.value === 'number' ? params.value : null)}
          </Typography>
        ),
      },
      {
        field: 'brutto',
        headerName: 'BRUTTO',
        minWidth: 130,
        flex: 1,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        renderCell: (params: GridRenderCellParams<InvoiceRow, 'brutto'>) => (
          <Typography>
            {formatCurrency(typeof params?.value === 'number' ? params.value : null)}
          </Typography>
        ),
      },
      {
        field: 'comment',
        headerName: 'Comment',
        minWidth: 200,
        flex: 2,
        renderCell: (params: GridRenderCellParams<InvoiceRow, 'comment'>) => (
          <Typography
            variant="body2"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: 300,
            }}
          >
            {params?.value || '-'}
          </Typography>
        ),
      },
    ],
    []
  )

  const handleExportCSV = () => {
    downloadCSV(invoices, `invoices-export-${new Date().toISOString().split('T')[0]}.csv`)
  }

  return (
    <Card>
      <Toolbar
        sx={{
          pl: 2,
          pr: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          minHeight: 64,
        }}
      >
        <Typography variant="h6">Invoices ({invoices.length})</Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<DownloadIcon />}
          onClick={handleExportCSV}
          disabled={invoices.length === 0}
        >
          Export CSV
        </Button>
      </Toolbar>

      <Box sx={{ height: 500, width: '100%' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50, 100]}
          loading={isLoading}
          disableRowSelectionOnClick
          getRowId={(row) => row._id}
          sortingOrder={['asc', 'desc']}
          sx={{
            border: 'none',
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: alpha('#1976d2', 0.05),
              '& .MuiDataGrid-columnHeaderTitle': {
                fontWeight: 600,
              },
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: alpha('#1976d2', 0.04),
            },
          }}
        />
      </Box>
    </Card>
  )
}
